























































































import { defineComponent, watch, ref, computed, getCurrentInstance } from "vue";
import { format, subDays, addDays, parse, isEqual } from "date-fns";
import locale from "date-fns/locale/de";
import enLocale from "date-fns/locale/en";
import UiButton from "@dpa-id-components/ui-button";
import "@dpa-id-components/ui-button/dist/UiButton.css";
import UiIcon from "@dpa-id-components/ui-icon";
import "@dpa-id-components/ui-icon/dist/UiIcon.css";
import UiTooltip from "@dpa-id-components/ui-tooltip";
import "@dpa-id-components/ui-tooltip/dist/UiTooltip.css";
import UiDateRangePicker from "@dpa-id-components/ui-date-range-picker";
import "@dpa-id-components/ui-date-range-picker/dist/UiDateRangePicker.css";
import { useRoute, useRouter } from "vue-router/composables";
import {
  createDesktopDatePickerProps,
  createMobileDatePickerProps,
  formatDateRange
} from "./createDatePickerProps";
import { DATE_FORMAT_ISO } from "@/constants/theme";
import useMatomoEvent from "@/composition/useMatomoEvent";
import { isMobile } from "@/utils/responsive";

export default defineComponent({
  components: {
    UiButton,
    UiDateRangePicker,
    UiIcon,
    UiTooltip
  },
  setup() {
    const root = getCurrentInstance();
    const { $i18n } = root.proxy;
    const route = useRoute();
    const router = useRouter();
    const query = computed(() => route.query);
    const displayDatePagination = ref(false);
    const dateRangeObject = ref<{
      startDate: Date;
      endDate: Date;
    }>({
      startDate: subDays(new Date(), 40),
      endDate: new Date()
    });
    // define dates for daterange label

    const today = format(new Date(), DATE_FORMAT_ISO);
    const yesterday = format(subDays(new Date(), 1), DATE_FORMAT_ISO);
    const lastFortyDays = format(subDays(new Date(), 40), DATE_FORMAT_ISO);
    const lastSevenDays = format(subDays(new Date(), 7), DATE_FORMAT_ISO);

    // define upper bound for non-selectable dates
    const targetDate = computed(() => {
      return subDays(today, 40);
    });

    /*
     * Create the right matomo event for change of Date event
     */
    function fireMatomoEvent(dateRange): void {
      const startDate = format(dateRange.startDate, DATE_FORMAT_ISO);
      const endDate = format(dateRange.endDate, DATE_FORMAT_ISO);

      if (startDate === lastFortyDays && endDate === today) {
        useMatomoEvent({
          category: "Top Navigation Bar",
          action: 'Click "Alles (Letzte 40 Tage)" within time-filter',
          name: "Time-Filter"
        });
      }

      if (startDate === today) {
        useMatomoEvent({
          category: "Top Navigation Bar",
          action: 'Click "Heute" within time-filter',
          name: "Time-Filter"
        });
      }

      if (startDate === yesterday && endDate === today) {
        useMatomoEvent({
          category: "Top Navigation Bar",
          action: 'Click "Seit gestern" within time-filter',
          name: "Time-Filter"
        });
      }

      if (startDate === lastSevenDays && endDate === today) {
        useMatomoEvent({
          category: "Top Navigation Bar",
          action: 'Click "Letzte 7 Tage" within time-filter',
          name: "Time-Filter"
        });
      }

      if (
        dateRange.startDate === dateRange.endDate &&
        dateRange.startDate !== today
      ) {
        useMatomoEvent({
          category: "Top Navigation Bar",
          action: "Click on a single day in calendar picker",
          name: "Time-Filter"
        });
      }

      if (startDate === endDate && startDate !== yesterday) {
        useMatomoEvent({
          category: "Top Navigation Bar",
          action: "Click on a single day in calendar picker",
          name: "Time-Filter"
        });
      }
    }

    /*
     * read dates from URL and check for valid Date
     */

    watch(query, value => {
      const startDate = parse(String(value.startDate));
      const endDate = parse(String(value.endDate));

      const isValidDate = computed(() => {
        return (
          startDate instanceof Date &&
          !Number.isNaN(Number(startDate)) &&
          endDate instanceof Date &&
          !Number.isNaN(Number(endDate))
        );
      });

      if (isValidDate.value) {
        if (!isEqual(dateRangeObject.value.startDate as Date, startDate)) {
          dateRangeObject.value.startDate = startDate;
        }
        if (!isEqual(dateRangeObject.value.endDate as Date, endDate)) {
          dateRangeObject.value.endDate = endDate;
        }
      }
    });

    watch(dateRangeObject, value => {
      const queryIsSameAsDateRange = computed(() => {
        return (
          query.value.startDate ===
            format(value.startDate as Date, DATE_FORMAT_ISO) &&
          query.value.endDate === format(value.endDate as Date, DATE_FORMAT_ISO)
        );
      });

      if (queryIsSameAsDateRange.value) {
        return;
      }
      fireMatomoEvent(value);
      router.push({
        query: {
          ...query.value,
          startDate: format(value.startDate as Date, DATE_FORMAT_ISO),
          endDate: format(value.endDate as Date, DATE_FORMAT_ISO)
        }
      });
    });

    /**
     * Logic for date pagination to
     * navigate to next and previous date
     */

    function handleSelectNextDay() {
      const getCurrentStartDate = new Date(String(query.value.startDate));
      const getNextStartDate = format(
        addDays(getCurrentStartDate, 1),
        DATE_FORMAT_ISO
      );

      const getNextEndDate = format(
        addDays(getCurrentStartDate, 1),
        DATE_FORMAT_ISO
      );

      if (query.value.startDate === query.value.endDate) {
        router.push({
          query: {
            ...query.value,
            startDate: getNextStartDate,
            endDate: getNextEndDate
          }
        });
      }
    }

    function handleSelectPreviousDay() {
      const getCurrentStartDate = new Date(String(query.value.startDate));
      const getCurrentEndDate = new Date(String(query.value.endDate));
      const getPrevStartDate = format(
        subDays(getCurrentStartDate, 1),
        DATE_FORMAT_ISO
      );

      const getPrevEndDate = format(
        subDays(getCurrentEndDate, 1),
        DATE_FORMAT_ISO
      );

      if (query.value.startDate === query.value.endDate) {
        router.push({
          query: {
            ...query.value,
            startDate: getPrevStartDate,
            endDate: getPrevEndDate
          }
        });
      }
    }

    /**
     * Display daterange label according to selected range,
     * send corresponding Matomo events
     */
    const currentLanguage = computed(() => {
      return $i18n.locale;
    });

    const selectedRangeLabel = computed(() => {
      const language = currentLanguage.value === "de" ? locale : enLocale;
      const languageSpecificDateFormat =
        currentLanguage.value === "de" ? "D. MMMM YYYY" : "MMMM D, YYYY";
      const mobileDateFormat =
        currentLanguage.value === "de" ? "DD.MM.YYYY" : "MM-DD-YYYY";

      const formatDateFrom = format(
        new Date(String(query.value.startDate)),
        isMobile(root.proxy as any)
          ? mobileDateFormat
          : languageSpecificDateFormat,
        {
          locale: language
        }
      );

      const formatDateTo = format(
        new Date(String(query.value.endDate)),
        isMobile(root.proxy as any)
          ? mobileDateFormat
          : languageSpecificDateFormat,
        {
          locale: language
        }
      );

      if (
        query.value.startDate === lastFortyDays &&
        query.value.endDate === today
      ) {
        displayDatePagination.value = false;

        return $i18n.t("lastFortyDays");
      }

      if (query.value.startDate === today) {
        displayDatePagination.value = true;

        return $i18n.t("today");
      }

      if (
        query.value.startDate === yesterday &&
        query.value.endDate === today
      ) {
        displayDatePagination.value = false;

        return $i18n.t("sinceYesterday");
      }

      if (
        query.value.startDate === lastSevenDays &&
        query.value.endDate === today
      ) {
        displayDatePagination.value = false;

        return $i18n.t("lastWeek");
      }

      if (
        query.value.startDate === query.value.endDate &&
        query.value.startDate !== today
      ) {
        displayDatePagination.value = true;

        return ` ${formatDateFrom}`;
      }

      if (
        query.value.startDate === query.value.endDate &&
        query.value.startDate !== yesterday
      ) {
        displayDatePagination.value = true;

        return formatDateFrom;
      }

      displayDatePagination.value = false;

      return ` ${formatDateFrom} - ${formatDateTo}`;
    });

    return {
      formatDateRange,
      dateRangeObject,
      datePickerPropsDesktop: createDesktopDatePickerProps(),
      datePickerPropsMobile: createMobileDatePickerProps(),
      selectedRangeLabel,
      displayDatePagination,
      handleSelectNextDay,
      handleSelectPreviousDay,
      useMatomoEvent,
      targetDate
    };
  }
});
